import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AssistantChatData } from '../../model';
import { UTextareaInteractiveComponent } from '@local/ui-infra';
import { KeyName } from '@local/ts-infra';
import { KeyboardHelperService } from '@shared/helper/keyboard-helper.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { BaseFile } from 'src/app/bar/services/blobs/blobs-storage.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UploadBlobService } from 'src/app/bar/services/blobs/upload-blob.service';
import { ChatFileService } from '../../services/chat-files.service';
import { isExtension } from '@local/common-web';

@UntilDestroy()
@Component({
  selector: 'chat-textarea',
  templateUrl: './chat-textarea.component.html',
  styleUrl: './chat-textarea.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatTextareaComponent implements OnInit, OnDestroy {
  private readonly KEY_HANDLER_PRIORITY = 4;
  private readonly UPLOADED_FILE_TOOLTIP = 'Attach files';
  private readonly DISABLED_UPLOADED_FILE_TOOLTIP = 'Maximum file limit reached';
  private readonly FAILED_FILE_TOOLTIP = 'Remove failed file/s to send';
  private readonly PENDING_FILE_TOOLTIP = 'File upload pending';
  private _query = '';
  private keyHandlerId: string;
  private keyboardHelperService: KeyboardHelperService;

  private get sendIcon() {
    return {
      value: 'send_fill',
      typeElement: this.isExtension ? 'secondary' : 'primary',
      styleType: 'fill',
      tooltipText: 'Send',
      iconStyles: {},
    };
  }

  private get stopIcon() {
    let styles = {};
    if (!this.isExtension) {
      styles = {
        boxShadow: '0px 2px 5px 0px rgba(209, 155, 255, 0.17)',
      };
    }
    return {
      value: 'stop-square',
      typeElement: 'secondary',
      styleType: this.isExtension ? 'fill' : 'outlined',
      tooltipText: 'Stop responding',
      styles,
      iconStyles: {
        fontSize: '12px',
      },
    };
  }

  readonly MAX_INPUT_WORDS = 6000;
  readonly TEXTAREA_PLACEHOLDER = 'Ask me about anything in';
  readonly FILE_TYPE_NOT_SUPPORTED_ERROR = 'File type not supported.';

  isExtension = isExtension();
  readonly isValidQuery = signal<boolean>(false);
  readonly isValidTextareaInput = signal<boolean>(true);
  readonly uploadedFile = signal<BaseFile[]>([]);
  readonly fileUploadTooltip = signal<string>(this.UPLOADED_FILE_TOOLTIP);
  readonly acceptFiles = signal<string[]>([]);

  chatId = input<string>();
  assistantData = input<AssistantChatData>();
  messageInprogress = input<boolean>();
  allowFileUpload = input<boolean>();
  initCompleted = input<boolean>();
  totalCountsFiles = input<number>(0);

  cancelQuery = output<void>();
  sendQuery = output<void>();
  queryChange = output<string>();
  onEscape = output();
  uploadFiles = output<File[]>();
  uploadError = output<string>();
  removeFile = output<string>();

  footerView = computed(() => this.uploadedFile()?.length || this.isExtension);

  maxFileUploaded = computed(() => this.totalCountsFiles() >= this.chatFileService.MAX_FILE_UPLOADED_ON_THREAD);
  private errorFileUploaded = computed(() => this.uploadedFile().some((file) => file.status === 'error'));
  private onUploadingFile = computed(() => this.uploadedFile().some((file) => file.status === 'uploading'));

  sendIconDisabled = computed(
    () =>
      !this.initCompleted() ||
      !this.isValidTextareaInput() ||
      this.errorFileUploaded() ||
      this.onUploadingFile() ||
      (!this.isValidQuery() && !this.uploadedFile()?.length && !this.messageInprogress())
  );

  settingIcon = computed(() => {
    if (this.messageInprogress()) {
      return this.stopIcon;
    } else {
      return this.sendIcon;
    }
  });

  settingIconTooltip = computed(() => {
    if (this.errorFileUploaded()) {
      return this.FAILED_FILE_TOOLTIP;
    }
    if (this.onUploadingFile()) {
      return this.PENDING_FILE_TOOLTIP;
    }
    if (this.sendIconDisabled()) {
      return '';
    }
    return this.settingIcon()?.tooltipText;
  });

  uTextareaInteractive = viewChild(UTextareaInteractiveComponent);

  set query(value: string) {
    if (!value) {
      this._query = '';
    } else {
      this._query = value;
    }
    this.queryChange.emit(this._query);
    this.isValidQuery.set(!!(this.query && this.query.trim()));
  }

  get query() {
    return this._query;
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  @HostBinding('attr.includeFiles')
  get includeFiles() {
    return !!this.uploadedFile()?.length;
  }

  constructor(
    private keyboardService: KeyboardService,
    private uploadBlobService: UploadBlobService,
    protected chatFileService: ChatFileService
  ) {
    this.acceptFiles.set(this.chatFileService.getAcceptedFiles());

    effect(
      () => {
        if (this.chatId())
          this.chatFileService
            .updatedFileStorage$(this.chatId())
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.uploadedFile.set(this.chatFileService.getFiles(this.chatId()) || []);
            });
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        if (this.maxFileUploaded()) {
          this.fileUploadTooltip.set(this.DISABLED_UPLOADED_FILE_TOOLTIP);
        } else {
          this.fileUploadTooltip.set(this.UPLOADED_FILE_TOOLTIP);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.setUpKeyboardHelperService();
  }

  ngOnDestroy() {
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
  }

  focusTextarea() {
    this.uTextareaInteractive()?.setFocus();
  }

  clearTextarea() {
    this.uTextareaInteractive()?.clearInput();
  }

  onFilterChanged() {
    this.focusTextarea();
  }

  handleSendQuery() {
    if (this.messageInprogress()) {
      this.cancelQuery.emit();
    } else {
      this.sendHandler();
    }
  }

  async sendHandler() {
    if (this.sendIconDisabled()) {
      return;
    }
    this.sendQuery.emit();
  }

  handleInputError(isValid: boolean) {
    this.isValidTextareaInput.set(isValid);
  }

  onQueryChange($event) {
    this.query = $event;
  }

  onUploadFiles(files: FileList) {
    this.uploadFiles.emit(Array.from(files || []));
  }

  onUploadError(message: string) {
    this.uploadBlobService.onUploadError(message);
  }

  setUpKeyboardHelperService() {
    this.keyboardHelperService = new KeyboardHelperService('horizontal');
    this.registerKeyHandler();
  }

  registerKeyHandler() {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler(
      (keys: Array<KeyName>, event: CustomKeyboardEvent) => this.handleKeys(keys, event),
      this.KEY_HANDLER_PRIORITY
    );
  }

  private async handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): Promise<void> {
    if (keys?.length > 1) return;
    const key = keys[0];
    switch (key) {
      case 'slash':
        if (!this?.uTextareaInteractive().isFocus()) {
          this.focusTextarea();
          event.preventDefault();
        }
        break;
      case 'escape':
        this.onEscape.emit();
    }
  }
}
